import React from 'react';
import { Spin } from 'antd';
import SelectWithInfiniteScroller from 'components/atoms/SelectWithInfiniteScroller';
import ConstraintsTable from './ConstraintsTable';
import ConstraintSelector from './ConstraintSelector';
import type { ConstraintTableColumn, ConstraintLevelOption } from '../../../../config/optimizePlanConfig';
import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';
import type { ConstraintRowData, Level } from './types';

interface ConstraintsTabProps {
    level: Level;
    setLevel: React.Dispatch<React.SetStateAction<Level>>;
    levelOptions: (ConstraintLevelOption & { disabled?: boolean })[];
    tableData: ConstraintRowData[];
    setTableData: React.Dispatch<React.SetStateAction<ConstraintRowData[]>>;
    tableHeaders: ConstraintTableColumn[];
    kpiList: { name: string; label: string }[];
    onKPISelectHandler: (selectedNames: string[]) => void;
    errorExists: boolean;
    constraintsLoading: boolean;
}

const ConstraintsTab: React.FC<ConstraintsTabProps> = ({
    level,
    setLevel,
    levelOptions,
    tableData,
    setTableData,
    tableHeaders,
    kpiList,
    onKPISelectHandler,
    errorExists,
    constraintsLoading,
}) => {
    const { constraintsSection, levelsLabel, selectLevelPlaceholder, tableError, emptyConstraints } =
        useTpoGenConfig().labels.optimizePlan;
    return (
        // Single card — one border, rounded corners, fills the full modal body height
        <div className="h-full flex flex-col overflow-hidden border border-[rgba(240,240,240,1)] rounded">
            {/* Section heading with a bottom separator */}
            <div className="px-4 py-3 border-b border-[rgba(240,240,240,1)] bg-white shrink-0">
                <p className="text-sm font-semibold text-[#140b1d]">{constraintsSection}</p>
            </div>

            {/* Controls row — same horizontal padding as the table cells */}
            <div className="flex justify-between items-end px-4 py-4 bg-white shrink-0">
                <div className="flex flex-col items-start w-[248px]">
                    <label className="text-sm text-[#18181b] pb-2 leading-[22px]">{levelsLabel}</label>
                    <SelectWithInfiniteScroller
                        hasMore={false}
                        showSearch={false}
                        filterOption={false}
                        placeholder={selectLevelPlaceholder}
                        className="!h-8 !w-full"
                        showArrow
                        onChange={(val: Level) => setLevel(val)}
                        value={level}
                        options={levelOptions.map((opt) => ({
                            value: opt.key,
                            label: opt.label,
                            disabled: opt.disabled ?? false,
                        }))}
                    />
                </div>
                <ConstraintSelector
                    items={kpiList}
                    selectedNames={tableData.map((d) => d.name)}
                    onApply={onKPISelectHandler}
                    disabled={!kpiList.length}
                    loading={constraintsLoading}
                />
            </div>

            {/* Table — no extra border, flows directly from controls, scrollable */}
            <div className="flex-1 min-h-0 overflow-hidden px-4 pb-3 pt-0">
                {errorExists ? (
                    <div className="flex justify-center items-center h-full text-red-600">{tableError}</div>
                ) : constraintsLoading ? (
                    <div className="flex justify-center items-center h-full">
                        <Spin />
                    </div>
                ) : tableData.length ? (
                    <ConstraintsTable
                        columns={tableHeaders}
                        data={tableData}
                        setData={setTableData}
                        isTopLevel={levelOptions.find((o) => o.key === level)?.depthLevel === 0}
                    />
                ) : (
                    <div className="flex justify-center items-center h-full text-[rgba(0,0,0,0.25)]">
                        {emptyConstraints}
                    </div>
                )}
            </div>
        </div>
    );
};

export default React.memo(ConstraintsTab);
